import type { IduraMode, IduraSettingsUpdateRequest } from "@repo/shared/types";
import { useState, useTransition } from "react";
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import type { Vendor } from "@/components/vendor-info";
import { patch } from "@/lib/api/_client";
import { useT } from "@/lib/i18n/use-t";
import { FeatureCard, type PriceLine } from "./feature-card";
import { T } from "./idura-settings.i18n";

const PRICES: PriceLine[] = [
  { label: "Idura Verify", unit: "Per login", price: "0.032 EUR" },
  { label: "Idura Signing", unit: "Per signature", price: "0.999 EUR" },
];

const VENDOR: Vendor = {
  name: "Idura ApS (formerly Criipto)",
  homepage: "https://idura.eu",
  details: ["CVR 35142207 (Denmark)", "Gammel Kongevej 3E, 1610 København V, Denmark"],
  // Idura publishes no public terms-of-service page.
  links: [
    { kind: "status", href: "https://status.idura.app/" },
    { kind: "security", href: "https://trustcenter.idura.eu/" },
  ],
};

type IduraSettingsProps = {
  iduraMode: IduraMode;
  mutate: () => void;
};

export function IduraSettings({ iduraMode: initialMode, mutate }: IduraSettingsProps) {
  const t = useT(T);
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [mode, setMode] = useState<IduraMode>(initialMode);

  const modeHint =
    mode === "disabled"
      ? t.usingDisabled
      : mode === "production"
        ? t.usingProdCreds
        : t.usingTestCreds;

  // Auto-save on change — no save button. Revert the toggle if the write is rejected.
  const handleModeChange = (value: string) => {
    if (!value || value === mode) return;
    const previous = mode;
    setMode(value as IduraMode);
    setError(null);
    setSuccess(null);

    startTransition(async () => {
      try {
        await patch("/internal/idura/settings", {
          iduraMode: value as IduraMode,
        } satisfies IduraSettingsUpdateRequest);
        setSuccess(t.settingsSaved);
        mutate();
      } catch (err: any) {
        setMode(previous);
        setError(err.message || t.failedToSave);
      }
    });
  };

  return (
    <FeatureCard
      title={t.title}
      description={t.description}
      prices={PRICES}
      vendor={VENDOR}
      footnote={t.pricesFootnote}
      error={error}
      success={success}
      control={
        <div className="flex flex-col items-end gap-1">
          <Label htmlFor="idura-mode" className="sr-only">
            {t.mode}
          </Label>
          <ToggleGroup
            id="idura-mode"
            type="single"
            value={mode}
            onValueChange={handleModeChange}
            disabled={isPending}
            className="w-fit"
          >
            <ToggleGroupItem value="disabled">{t.disabled}</ToggleGroupItem>
            <ToggleGroupItem value="test">{t.test}</ToggleGroupItem>
            <ToggleGroupItem value="production">{t.production}</ToggleGroupItem>
          </ToggleGroup>
          <p className="text-xs text-muted-foreground text-right max-w-[16rem]">{modeHint}</p>
        </div>
      }
    />
  );
}
